import { Height as HeightIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { activityApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { formatDateTime } from "helpers/dateTimeHelper";
import { usePatientActivity } from "hooks/usePatientActivity";
import React, { useCallback, useMemo } from "react";

import {
  MetricValueResponseDto,
  MetricValuesPaginationRequestDtoHealthProviderCodeEnum,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "../../libs/cellera-sdk";

export const HEIGHT_QUERY_KEY = "activity-height";

export interface HeightTabContentProps {
  patientId: string | undefined;
}

const HeightTabContent: React.FC<HeightTabContentProps> = ({ patientId }) => {
  const columns: CustomTableColumn<MetricValueResponseDto>[] = useMemo(
    () => [
      {
        id: "value",
        label: "Height",
        getValue: (row) => {
          const value = row.valueFloat ?? row.valueInt ?? row.valueText;
          return value == null ? "-" : `${value} ft`;
        },
        width: "50%",
      },
      {
        id: "recordedAt",
        label: "Recorded Time",
        getValue: (row) => formatDateTime(row.startDate),
        width: "50%",
      },
    ],
    []
  );

  const fetchPage = useCallback(
    async (page: number, pageSize: number) => {
      const response = await activityApi.getMetricValuesPaginatedByPatientId({
        patientId: patientId as string,
        metricValuesPaginationRequestDto: {
          page: page + 1,
          pageSize,
          metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.Height,
          healthProviderCode:
            MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
        },
      });
      return {
        data: response.data as MetricValueResponseDto[],
        total: response.pagination.total ?? 0,
      };
    },
    [patientId]
  );

  const {
    data,
    total,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    isLoading,
    onPageChange,
    onRowsPerPageChange,
  } = usePatientActivity<MetricValueResponseDto>({
    patientId,
    queryKey: HEIGHT_QUERY_KEY,
    initialPageSize: 10,
    fetchPage,
  });

  return (
    <CustomTable<MetricValueResponseDto>
      data={data}
      columns={columns}
      emptyStateContent={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <HeightIcon
            sx={{ color: "text.disabled", fontSize: 48, opacity: 0.5 }}
          />
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            No height data available
          </Typography>
        </Box>
      }
      getRowKey={(row) => row.metricId}
      totalCount={total}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      isLoading={isLoading}
      loadingMessage="Loading height data..."
      tableContainerSx={{
        height: "calc(100vh - 370px)",
      }}
    />
  );
};

export default HeightTabContent;
